
import React from 'react';
import api from '../api';

/**
 * Global License Overlay (Modal)
 * - Mount once at app-shell level.
 * - Opens when:
 *     - window.dispatchEvent(new CustomEvent('open-license'))
 *     - any link to "/license" is clicked (we intercept globally)
 * - Fetches license info on open and displays it.
 */
export default function LicenseOverlay() {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [data, setData] = React.useState(null);

  // Helpers for formatting dates (dd Mon yyyy)
  const pad2 = (n) => String(n).padStart(2, '0');
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const fmtDateOnly = (val) => {
    if (!val) return '';
    const dt = new Date(val);
    if (isNaN(dt.getTime())) return String(val);
    return `${pad2(dt.getDate())} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
  };

  const fetchLicense = async () => {
    setLoading(true); setError(null);
    try {
      const resp = await api.get('/license/status');
      setData(resp.data || resp);
    } catch (e) {
      setError(e?.message || 'Failed to load license');
    } finally {
      setLoading(false);
    }
  };

  const openOverlay = () => {
    setOpen(true);
    fetchLicense();
  };

  React.useEffect(() => {
    const onOpen = () => openOverlay();
    window.addEventListener('open-license', onOpen);

    // Intercept clicks to "/license"
    const onClick = (e) => {
      const anchor = e.target.closest && e.target.closest('a[href]');
      if (!anchor) return;
      const url = new URL(anchor.href, window.location.origin);
      if (url.pathname === '/license') {
        e.preventDefault();
        e.stopPropagation();
        openOverlay();
      }
    };
    document.addEventListener('click', onClick, true);

    // Provide an imperative helper
    window.openLicense = () => window.dispatchEvent(new CustomEvent('open-license'));

    return () => {
      window.removeEventListener('open-license', onOpen);
      document.removeEventListener('click', onClick, true);
      delete window.openLicense;
    };
  }, []);

  if (!open) return null;

  return (
    <div className="overlay-backdrop" style={styles.backdrop} role="dialog" aria-modal="true">
      <div className="overlay-card" style={styles.card}>
        <div style={styles.header}>
          <h2 style={{margin:0}}>License</h2>
          <button onClick={()=>setOpen(false)} style={styles.closeBtn} aria-label="Close">×</button>
        </div>
        <div style={styles.body}>
          {loading && <div>Loading license...</div>}
          {error && <div className="alert error">{String(error)}</div>}
          {!loading && !error && data && (
            <div>
              
<table className="table" style={{width:'100%'}}>
  <tbody>
    <tr><th style={{width:'220px'}}>Shop / Organisation ID</th><td><code>{data.tenant_id}</code></td></tr>
    <tr><th>Shop / Organisation Register Name</th><td>{data.name}</td></tr>
    <tr><th>Shop / Organisation Name</th><td><code>{data.slug}</code></td></tr>
    <tr><th>License Valid From</th><td>{fmtDateOnly(data.license_valid_from || data.valid_from)}</td></tr>
    <tr><th>License Valid Till</th><td>{fmtDateOnly(data.license_valid_till || data.valid_till)}</td></tr>
    <tr><th>Status</th><td>{String(data.status || '').toUpperCase()}{data.requires_read_only ? ' (READ-ONLY)' : ''}</td></tr>
    <tr><th>Days Left</th><td>{data.days_left} day{data.days_left===1?'':'s'}</td></tr>
  </tbody>
</table>

              {data.requires_read_only &&(
                <div className="alert error" style={{marginTop:'12px'}}>Your license has expired. The app is in read-only mode.</div>
              )}
            </div>
          )}
        </div>
        <div style={styles.footer}>
          <button onClick={()=>setOpen(false)} className="btn">Close</button>
        </div>
      </div>
    </div>
  );
}


const styles = {
  backdrop: {
    position:'fixed', inset:0, background:'rgba(0,0,0,0.35)',
    display:'flex', alignItems:'center', justifyContent:'center', zIndex: 9999
  },
  card: {
    width:'min(720px, 92vw)',
    background:'var(--bg-elev)',
    color:'var(--text)',
    borderRadius:'16px',
    boxShadow:'var(--shadow-lg)',
    border:'1px solid rgba(2,6,23,0.08)'
  },
  header: {
    display:'flex', alignItems:'center', justifyContent:'space-between',
    padding:'14px 16px', borderBottom:'1px solid rgba(2,6,23,0.08)'
  },
  body: { padding:'16px' },
  footer: { padding:'12px 16px', borderTop:'1px solid rgba(2,6,23,0.08)', textAlign:'right' },
  closeBtn: { fontSize:'22px', lineHeight:'22px', background:'transparent', color:'inherit', border:'none', cursor:'pointer' }
};

